import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore, navItemsStore } from '@/stores/auth';

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { public: true },
  },
  {
    // 只读分享查看页（免登录；token 即凭证，后端 /api/share/pub/* 公开面）。
    // 必须在守卫里对 /s/ 前缀显式放行：插件路由动态注册会命中 not-found 兜底
    // （meta.public=true），不能用 meta 判定——与 /login 同款前缀判断。
    path: '/s/:token',
    name: 'share-view',
    component: () => import('@/views/ShareView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'root',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      // P3.5 平台级数据源页（v1 kb DataSourceManager 上移；设计 §9.3——唯一数据源管理入口）
      {
        path: 'datasource',
        name: 'datasource',
        component: () => import('@/views/DataSource.vue'),
        meta: { title: '数据源' },
      },
      // 平台使用说明（host 内置页，2026-11-02）：平台价值介绍 + 各能力入口导航。
      // 纯静态内容，无 API 依赖；导航项在 stores/auth.js loadPlugins 里登记（order 90）。
      {
        path: 'guide',
        name: 'guide',
        component: () => import('@/views/GuideView.vue'),
        meta: { title: '使用说明' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/Login.vue'),
    meta: { public: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 全局守卫
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // 未登录：允许 /login 与 /s/（分享查看，token 即凭证），其余一律跳登录。
  // 注意：不能在这里用 to.meta.public 放行——因为插件路由是动态注册的，
  // 刷新 /pve-mgr 时该路径尚未注册，会命中 not-found 兜底（其 meta.public=true），
  // 导致直接放行、跳过 loadPlugins，页面渲染成 Login.vue（表现为刷新跳登录页）。
  if (!auth.token) {
    if (to.path === '/login' || to.path.startsWith('/s/')) return true;
    return { path: '/login' };
  }

  // 已登录：首次访问需加载插件清单并动态注册路由
  if (!auth.pluginsLoaded) {
    await auth.loadPlugins(router);
    // 插件路由是 addRoute 动态注入的，当前导航在导航开始时已用旧路由表
    // 解析过 to，addRoute 不会让当前导航重新匹配。返回当前完整地址强制重新
    // 触发一次导航，让 to 按新路由表重新解析。
    // 必须返回 fullPath 字符串而非 { path }：path 字段不允许带 query——
    // vue-router 会丢弃 ?xxx 部分（直链带 query 刷新/首次进入时 query 静默丢失，
    // 隐藏页 conv= 参数拿不到即此因，2026-10-14 实测）。
    return to.fullPath;
  }

  // 根路径落点：v2 无 dashboard 插件（P3.7 迁移），跳第一个已注册插件页；
  // 无任何插件页时留在 /（渲染空布局）。
  if (to.path === '/') {
    const first = navItemsStore.value[0];
    if (first) return { path: first.path, replace: true };
    return true;
  }

  return true;
});

export default router;
